import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRandomQuestions } from '../utils/gkQuestions';
import { playCorrectSound, playWrongSound } from '../utils/soundEffects';
import { X, ChevronRight } from 'lucide-react';
import confetti from 'canvas-confetti';

const OPTION_COLORS = [
  { bg: 'bg-[#FF9933]', hover: 'hover:bg-[#ff8010]', border: 'border-[#cc7a29]', text: 'text-white' },
  { bg: 'bg-[#138808]', hover: 'hover:bg-[#0f6d06]', border: 'border-[#0e6506]', text: 'text-white' },
  { bg: 'bg-[#000080]', hover: 'hover:bg-[#0000a0]', border: 'border-[#000060]', text: 'text-white' },
  { bg: 'bg-[#8B1A1A]', hover: 'hover:bg-[#a02020]', border: 'border-[#6d1414]', text: 'text-white' },
];

export default function GKPlayPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const scoreRef = useRef(0);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    setQuestions(getRandomQuestions(20));
  }, []);

  const currentQ = questions[currentIndex];

  const handleOptionClick = (optionIndex) => {
    if (showAnswer) return;
    setSelectedOption(optionIndex);
    setShowAnswer(true);

    const isCorrect = optionIndex === currentQ.correctIndex;
    if (isCorrect) {
      setScore((s) => s + 1);
      playCorrectSound();
      confetti({ particleCount: 40, spread: 50, origin: { y: 0.7 }, colors: ['#FF9933', '#138808', '#000080'] });
    } else {
      playWrongSound();
    }

    setAnswers((prev) => [...prev, { questionId: currentQ.id, selected: optionIndex, correct: currentQ.correctIndex, isCorrect }]);
  };

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      navigate(`/gk-quiz/results?score=${scoreRef.current}&total=${questions.length}`);
      return;
    }
    setCurrentIndex((i) => i + 1);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  if (!currentQ) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="font-heading text-2xl text-gray-400">Loading...</p>
      </div>
    );
  }

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-100 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              data-testid="gk-score"
              className="font-heading text-2xl font-black"
              style={{ color: '#FF9933' }}
            >
              {score}/{questions.length}
            </span>
          </div>
          <span
            data-testid="gk-question-counter"
            className="font-body text-sm font-bold text-gray-500 bg-gray-100 px-3 py-1 rounded-full"
          >
            {currentIndex + 1} of {questions.length}
          </span>
          <button
            data-testid="gk-quit"
            onClick={() => navigate('/gk-quiz')}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Progress bar - tricolour */}
      <div className="h-2 bg-gray-200">
        <div
          data-testid="gk-progress"
          className="h-full transition-all duration-500 ease-out rounded-r-full"
          style={{
            width: `${progress}%`,
            background: 'linear-gradient(90deg, #FF9933 0%, #FFFFFF 50%, #138808 100%)',
          }}
        />
      </div>

      {/* Question area */}
      <div className="flex-1 flex flex-col max-w-2xl mx-auto w-full px-4 py-6">
        {/* Category badge */}
        <div className="text-center mb-2">
          <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
            {currentQ.category}
          </span>
        </div>

        {/* Question */}
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-8 mb-6 text-center">
          <p
            data-testid="gk-question-text"
            className="font-heading text-xl sm:text-2xl font-bold text-gray-900 leading-snug"
          >
            {currentQ.question}
          </p>
        </div>

        {/* Options */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {currentQ.options.map((option, i) => {
            const optKey = `${currentQ.id}-opt-${option}`;
            const color = OPTION_COLORS[i];
            let extraClass = '';
            if (showAnswer) {
              if (i === currentQ.correctIndex) {
                extraClass = 'ring-4 ring-green-400 scale-[1.02]';
              } else if (i === selectedOption && i !== currentQ.correctIndex) {
                extraClass = 'opacity-60 ring-4 ring-red-400';
              } else {
                extraClass = 'opacity-40';
              }
            }

            return (
              <button
                key={optKey}
                data-testid={`gk-option-${i}`}
                onClick={() => handleOptionClick(i)}
                disabled={showAnswer}
                className={`${color.bg} ${!showAnswer ? color.hover : ''} ${color.text} font-bold text-base sm:text-lg py-4 px-5 rounded-2xl border-b-[5px] ${color.border} ${!showAnswer ? 'active:border-b-0 active:translate-y-[5px]' : ''} transition-all shadow-md text-left ${extraClass}`}
              >
                <span className="opacity-60 mr-2 text-sm">{String.fromCharCode(65 + i)}.</span>
                {option}
              </button>
            );
          })}
        </div>

        {/* Feedback + Next */}
        {showAnswer && (
          <div className="text-center animate-pop-in">
            {selectedOption === currentQ.correctIndex ? (
              <p data-testid="gk-feedback-correct" className="font-heading text-xl font-black text-green-600 mb-3">
                Correct!
              </p>
            ) : (
              <p data-testid="gk-feedback-wrong" className="font-heading text-base font-bold text-red-500 mb-3">
                The answer is: <span className="text-green-700">{currentQ.options[currentQ.correctIndex]}</span>
              </p>
            )}
            <button
              data-testid="gk-next"
              onClick={handleNext}
              className="bg-[#000080] text-white font-black text-base py-3 px-8 rounded-full border-b-[4px] border-[#000060] active:border-b-0 active:translate-y-[4px] transition-all hover:bg-[#0000a0] inline-flex items-center gap-2"
            >
              {currentIndex + 1 >= questions.length ? 'See Results' : 'Next Question'}
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
